import { BackgroundTaskType } from "@api/progress-api/progress-api-types";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { FaroTable } from "@components/common/faro-table/faro-table";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { SdbBackgroundTask } from "@custom-types/sdb-background-tasks-types";
import { useDateTime } from "@hooks/use-date-time";
import { getProjectBackgroundTasksColumns } from "@pages/project-details/project-bg-tasks/project-background-tasks-columns";
import { BackgroundTasksTableHeaders } from "@pages/project-details/project-bg-tasks/project-background-tasks-table-utils";
import {
  sdbBackgroundTaskBeforePageSelector,
  totalCountSelector,
  totalCountByTypeSelector,
} from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { updateBackgroundTasks } from "@store/sdb-background-tasks/sdb-background-tasks-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useEffect } from "react";

interface Props extends BaseProjectIdProps {
  /** List of background tasks */
  sdbBackgroundsTasks: SdbBackgroundTask[];

  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /** All the columns that we want to show. The order defines the order of the columns */
  requiredColumns: BackgroundTasksTableHeaders[];

  /** Optional type of tasks that should be displayed in the table */
  taskType?: BackgroundTaskType;
}

/**
 * List of the background tasks for the currently selected project
 */
export function ProjectBackgroundTasksTable({
  sdbBackgroundsTasks,
  isLoading,
  requiredColumns,
  projectId,
  taskType,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const progressApiClient = useProgressApiClient({
    projectId,
  });
  const projectApiClient = useProjectApiClient({ projectId });

  const beforePage = useAppSelector(sdbBackgroundTaskBeforePageSelector);
  const totalCount = useAppSelector(totalCountSelector);

  /**
   * Always call the selector hook, but only use it when taskType is provided
   * We use a default taskType if none is provided to avoid conditional hook calls
   */
  const effectiveTaskType = taskType || "VideoMode";
  const totalCountForTaskType = useAppSelector(
    totalCountByTypeSelector(effectiveTaskType)
  );

  const { formatDate } = useDateTime();

  // Make sure the correct table type is set in the store and reset the bulk action state after unmounting
  useEffect(() => {
    dispatch(setTableType(TableType.backgroundTasks));

    return () => {
      dispatch(resetTableState());
    };
  }, [dispatch]);

  // Use the appropriate total count based on task type
  // When filtering by task type, use the type-specific count to avoid conflicts with useDataManagement
  const displayTotalCount = taskType ? totalCountForTaskType : totalCount;

  return (
    <FaroTable
      entities={sdbBackgroundsTasks}
      uniqueIdKey="id"
      isLoading={isLoading}
      columns={requiredColumns.map(
        (requiredColumn) =>
          getProjectBackgroundTasksColumns(formatDate)[requiredColumn]
      )}
      rowHeight={54}
      shouldHideCheckbox={true}
      totalCount={displayTotalCount ?? undefined}
      onNextPage={async () => {
        await dispatch(
          updateBackgroundTasks({
            progressApiClient,
            projectApiClient,
            beforePage,
            taskType,
          })
        );
      }}
      hasMoreItems={beforePage !== null}
      tableType={TableType.backgroundTasks}
      paginationMode="server"
    />
  );
}
